import throttle from 'raf-throttle'
import {
  template,
  UICorePlugin,
  type EventMap,
  type Strings,
  type TemplateStringifier,
  type SupportedVersion,
  $
} from '@clappr/core'
import {Events} from 'common/types/events'
import type {Options} from 'common/types/options'
import type {ShareConfig} from 'common/api/records'
import type {MediaControl} from 'common/plugins/media-control'
import type {Notifier} from 'common/plugins/notifier'
import * as skins from './skins'

type ShareNetworks = Record<string, (url: string) => string>

export class Share<T extends Options> extends UICorePlugin<T> {
  private static FULL_HEIGHT_THRESHOLD = 45

  private networks: ShareNetworks = {
    vk: (url) => `https://vk.com/share.php?url=${url}`,
    facebook: (url) => `https://www.facebook.com/sharer.php?u=${url}`,
    ok: (url) => `https://connect.ok.ru/offer?url=${url}`,
    twitter: (url) => `https://twitter.com/intent/tweet?url=${url}`,
    livejournal: (url) =>
      `https://www.livejournal.com/post/?extShare=${encodeURIComponent(
        '{"href": "'
      )}${url}${encodeURIComponent('"}')}&owner_post_version=1`,
    'lj-embed': () =>
      `https://livejournal.com/update.bml?event=${encodeURIComponent(
        this.embedCode
      )}`
  }

  private clickCopy = false
  private shouldPlayOnClose = false
  private $container?: ZeptoCollection
  private $copyBtn!: ZeptoCollection
  private $embedCode!: ZeptoCollection
  private $embedLink!: ZeptoCollection
  private $content!: ZeptoCollection

  private static template = ''
  private static classNames: Record<string, string> = {}

  public static async preload(options: Options): Promise<void> {
    if (options.share && !Share.template) {
      const {share, mediaControl} = options
      const loadSkin =
        (share?.skinName && (skins as any)[share?.skinName]) ||
        (mediaControl?.skinName && (skins as any)[mediaControl?.skinName]) ||
        skins.classic

      if (typeof loadSkin !== 'function') {
        return
      }

      const {default: template, classNames} = await loadSkin()

      if (template) {
        Share.template = template
      }

      if (classNames) {
        Share.classNames = classNames
      }
    }
  }

  public get name(): string {
    return 'share'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get events(): EventMap {
    // TODO(VP-4598): Избавиться от использования классов для определения обработчика события
    // Использование дата аттрибутов имеет приоритет
    return {
      'click .eplayer-share-close, [data-share-close]': 'hide',
      'click [data-share-network]': 'share',
      'click [data-share-embed]': 'copyEmbedCode',
      'click #copy-btn': 'copy'
    }
  }

  public get params(): ShareConfig {
    return this.core.options.share || ({} as ShareConfig)
  }

  private get classNames(): Record<string, string> {
    return Share.classNames
  }

  public get template(): TemplateStringifier {
    return template(Share.template)
  }

  private get i18n(): Strings<Options> {
    return this.core.i18n
  }

  public get shareUrl(): string {
    const {playList, initialId, allFrontSettings} = this.core.options
    const shareUrl = playList?.clickUrl || playList?.defaultShareUrl || ''

    if (!playList || !shareUrl) return ''

    const url = new URL(
      shareUrl.match(/^https?:\/\//i)
        ? shareUrl
        : `https://${shareUrl.replace(/^\/\//, '')}`
    )

    const shareParams = {
      id: initialId,
      recordId: playList.uuid ?? playList.id
    }

    const utmParams = new URLSearchParams(
      allFrontSettings?.playerTemplate?.utmParams ?? undefined
    )

    utmParams.forEach((value, key) => {
      url.searchParams.append(key, value)
    })

    return url
      .toString()
      .concat(`#rprecord=${btoa(JSON.stringify(shareParams))}`)
  }

  public get embedCode(): string {
    const {playList} = this.core.options

    if (!playList?.embedCode) {
      return ''
    }

    return playList.embedCode
      .replace('%WIDTH%', '640')
      .replace('%HEIGHT%', '360')
      .replace('%AUTOPLAY%', 'false')
      .replace('%RID%', `${playList.id}`)
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  private get notifier(): Notifier<T> | null {
    return this.core.getPlugin('notifier') as Notifier<T> | null
  }

  private get shouldToggleControls(): boolean {
    return (
      !this.$container?.length &&
      !this.$el.hasClass('eplayer-hide-control-start')
    )
  }

  public bindEvents(): void {
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    if (this.mediaControl) {
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_RENDERED,
        this.render
      )
      this.listenTo(this.mediaControl, Events.MEDIACONTROL_SHARE, this.show)
    }
  }

  private onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
    this.shouldPlayOnClose = false
  }

  private toggleFullHeight(fullHeight = false): void {
    this.$el.toggleClass(
      this.classNames.fullHeight,
      fullHeight ??
        this.core.el.offsetHeight - this.$content.height() <
          Share.FULL_HEIGHT_THRESHOLD
    )
  }

  private onParentResize = throttle(() => {
    this.toggleFullHeight()
  })

  private show(): void {
    if (this.shouldToggleControls) {
      this.$el.show()

      if (this.$content?.length) {
        this.toggleFullHeight()
        this.listenTo(this.core, Events.CORE_RESIZE, this.onParentResize)
      }

      this.trigger(Events.SHARE_OPEN)

      const {activeContainer} = this.core

      if (activeContainer?.isPlaying()) {
        this.shouldPlayOnClose = true
        activeContainer.pause()
      }

      if (activeContainer) {
        this.listenTo(activeContainer, Events.CONTAINER_PLAY, this.hide)
      }
    }
  }

  protected hide(): void {
    if (this.shouldToggleControls) {
      this.stopListening(this.core, Events.CORE_RESIZE, this.onParentResize)
      this.$el.hide()
      this.toggleFullHeight(false)

      const {shareSuccess} = this.classNames

      // use `toggleClass`
      // `removeClass` removes classname if names argument is undefined
      this.$el.find(`.${shareSuccess}`).toggleClass(shareSuccess, false)

      const {activeContainer} = this.core

      if (this.shouldPlayOnClose) {
        this.shouldPlayOnClose = false
        activeContainer?.play()
      }

      this.trigger(Events.SHARE_CLOSE)

      if (activeContainer) {
        this.stopListening(activeContainer, Events.CONTAINER_PLAY, this.hide)
      }
    }
  }

  protected share(event: MouseEvent): Window {
    const {shareNetwork = ''} = (event.currentTarget as HTMLElement).dataset
    const url = this.getShareUrl(shareNetwork, this.shareUrl)

    this.trigger(Events.SHARE_SOCIALNETWORK, {
      network: shareNetwork,
      shareUrl: this.shareUrl
    })

    return window.open(url, '_blank') as Window
  }

  protected copy(): void {
    if (!this.clickCopy) {
      this.clickCopy = true
      this.$embedCode.focus()
      document.execCommand('SelectAll')

      try {
        document.execCommand('Copy', false)
        this.$copyBtn.addClass('copyed')
      } catch {
        throw new Error("Method 'Copy' doesn't work")
      }
    } else {
      this.$copyBtn.removeClass('copyed')
      this.clickCopy = false
    }

    this.trigger(Events.SHARE_EMBED)
  }

  private copyValue(value: string): void {
    const $copyElement = $('<textarea />')

    try {
      $copyElement
        .attr('readonly', true)
        .attr('tabindex', -1)
        .css({position: 'absolute', left: -9999})
        .val(value)
      $('body').append($copyElement)
      $copyElement.focus()
      document.execCommand('SelectAll')
      document.execCommand('Copy', false)
    } finally {
      $copyElement.remove()
    }
  }

  protected copyEmbedCode(event: MouseEvent): void {
    const $target = $(event.currentTarget)
    const param = $target.data('share-embed')
    const {shareSuccess} = this.classNames
    let value = ''
    let message = ''

    if (param === 'link') {
      value = this.shareUrl
      message = this.i18n.t('share_link_copied')
      this.trigger(Events.SHARE_LINK)
    } else if (param === 'code') {
      value = this.embedCode
      message = this.i18n.t('share_code_copied')
      this.trigger(Events.SHARE_EMBED)
    }

    this.$el.find(`.${shareSuccess}`).toggleClass(shareSuccess, false)

    if (value) {
      this.copyValue(value)
      $target.toggleClass(shareSuccess, true)
      this.notifier?.showMessage(message || this.i18n.t('share_copied'))
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public render(): this {
    if (!this.mediaControl) return this

    // TODO(VP-4598): Избавиться от использования классов для поиска элементов
    // Использование дата аттрибутов имеет приоритет
    const $el = $(
      this.template(
        [
          'share',
          'share_copy',
          'share_link',
          'share_copy_link',
          'share_code',
          'share_copy_code'
        ].reduce(
          (res, key) => ({
            ...res,
            [key]: this.i18n.t(key)
          }),
          {} as Record<string, string>
        )
      )
    )

    this.$el.children().remove()
    this.$el
      .attr('class', $el.attr('class'))
      .css('display', $el.css('display')?.trim())
      .append($el.children())

    this.$container = this.mediaControl.$el.find('.eplayer-skin-title-main')

    const {embedCodes, socialNetworks} = this.params
    const hasSocialNetworks = Boolean(this.shareUrl && socialNetworks)
    const hasEmbedCodes = Boolean(this.embedCode && embedCodes)

    this.$el.toggleClass(this.classNames.shareHasSocial, hasSocialNetworks)
    this.core.$el.toggleClass(
      'eplayer-with-sharing',
      hasSocialNetworks || hasEmbedCodes
    )

    if (!hasEmbedCodes && !hasSocialNetworks) {
      this.mediaControl.$el.find('[data-share]').remove()

      return this
    }

    if (!hasSocialNetworks) {
      this.$el
        .find('.eplayer-share-social-button, [data-share-network]')
        .remove()
    }

    const $embed = this.$el.find('.eplayer-share-embed, [data-share-embed]')

    if (!hasEmbedCodes) {
      $embed.remove()
    }

    if (!hasSocialNetworks && $embed.length === 0) {
      return this
    }

    if (this.$container?.length) {
      this.$container.append(this.el)

      return this
    }

    if (hasEmbedCodes) {
      this.$embedCode = this.$el.find('#share-code')
      this.$embedLink = this.$el.find('#share-link')
      this.$copyBtn = this.$el.find('#copy-btn')

      if (this.$embedCode.length > 0) {
        this.$embedCode.val(this.embedCode)
      }

      if (this.$embedLink.length > 0) {
        this.$embedLink.val(this.shareUrl)
      }
    }

    this.$content = this.$el.find('[data-share-content]')

    this.mediaControl.$el.append(this.el)

    return this
  }

  public getShareUrl(network: string, link: string): string | undefined {
    return this.networks[network]
      ? this.networks[network](encodeURIComponent(link))
      : undefined
  }
}
